'use client';

import { ArrowRight01Icon, Calendar03Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  addDays,
  differenceInCalendarDays,
  endOfWeek,
  format as formatDate,
  isValid,
  startOfWeek,
} from 'date-fns';
import * as React from 'react';

import { Button } from './button';
import { Calendar } from './calendar';
import { cn } from './lib/utils';
import { parseDateInput } from './parse-date-input';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from './popover';

export type DateRegion = 'EU' | 'US';

/**
 * Region defaults — note that `weekStartsOn` matches the picker convention
 * used by react-day-picker / date-fns: 0 = Sunday, 1 = Monday, … 6 = Saturday.
 */
export const REGION_DEFAULTS: Record<DateRegion, { dateFormat: string; weekStartsOn: 0 | 1 }> = {
  EU: { dateFormat: 'dd/MM/yyyy', weekStartsOn: 1 },
  US: { dateFormat: 'MM/dd/yyyy', weekStartsOn: 0 },
};

export type DateRange = { from: Date; to: Date };

/** The two fields of a range picker. */
export type DateRangeField = 'from' | 'to';

/**
 * What the calendar popover lines up with. `'trigger'` anchors it to the
 * calendar icon at the end of the control; `'field'` anchors it to the whole
 * control, so its left edge meets the left edge of the (first) input.
 */
export type DatePickerPopoverAnchor = 'trigger' | 'field';

export type WeekStartsOn = 0 | 1 | 2 | 3 | 4 | 5 | 6;

interface CommonProps {
  region?: DateRegion;
  /** date-fns format string. Defaults to the region's format. */
  dateFormat?: string;
  /**
   * 0 = Sunday … 6 = Saturday. Defaults to the region's first day
   * (EU = Monday, US = Sunday).
   */
  weekStartsOn?: WeekStartsOn;
  /** Number of months shown when the picker is open. Defaults to 2. */
  numberOfMonths?: number;
  /** Ignored in range mode, where `fromLabel` / `toLabel` fill the fields. */
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** In range mode this is the From field's id; the To field gets `${id}-to`. */
  id?: string;
  name?: string;
  /** Auto-focus the text input on mount (useful when mounted as a cell editor). */
  autoFocus?: boolean;
  /**
   * Open the calendar popover immediately on mount. Useful when the picker
   * is rendered as a cell editor so the user sees the full calendar without
   * having to click the trigger. Uncontrolled — subsequent open/close state
   * is still managed internally.
   */
  defaultOpen?: boolean;
  /** Defaults to `'trigger'`. */
  popoverAnchor?: DatePickerPopoverAnchor;
  /** In range mode these label the group; the fields carry `fromLabel` / `toLabel`. */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  /**
   * Override the rendered display text (overrides `dateFormat`). Range mode
   * calls it once per field with that field's `Date` (or `null`).
   */
  formatValue?: (
    value: Date | DateRange | null,
    ctx: { format: string; region: DateRegion },
  ) => string;
  /** Disable specific dates in the calendar. Forwarded to react-day-picker. */
  disabledDates?: React.ComponentProps<typeof Calendar>['disabled'];
}

export interface SingleDatePickerProps extends CommonProps {
  mode?: 'single';
  value?: Date | null;
  defaultValue?: Date | null;
  onValueChange?: (value: Date | null) => void;
}

export interface RangeDatePickerProps extends CommonProps {
  mode: 'range';
  value?: DateRange | null;
  defaultValue?: DateRange | null;
  /**
   * Fires only with a complete range — never with a start that has no end yet.
   * With `requireApply`, only on Apply.
   */
  onValueChange?: (value: DateRange | null) => void;
  /** Placeholder and accessible name of the start field. Defaults to "From". */
  fromLabel?: string;
  /** Placeholder and accessible name of the end field. Defaults to "To". */
  toLabel?: string;
  /**
   * Holds the clicks as a draft that only an Apply button under the calendar
   * commits, so a caller whose range is costly to read sees one change per
   * applied range rather than one per click. Closing the calendar any other
   * way discards the draft.
   */
  requireApply?: boolean;
  /** The Apply button's label. Defaults to "Apply". */
  applyLabel?: string;
  /**
   * The longest range, counting both ends (a single day is 1). While the next
   * click sets the finish, days that would stretch the range past it are
   * disabled, and a new start too far from the finish it would keep starts a
   * fresh range instead.
   */
  maxDays?: number;
  /** Explains `maxDays` under the calendar. Defaults to "A range is at most {maxDays} days". */
  maxDaysHint?: string;
}

export interface WeekDatePickerProps extends CommonProps {
  mode: 'week';
  value?: DateRange | null;
  defaultValue?: DateRange | null;
  onValueChange?: (value: DateRange | null) => void;
  /** Controlled count of weeks to span. */
  weeks?: number;
  /** Initial count of weeks when uncontrolled. Defaults to 1. */
  defaultWeeks?: number;
  /** Notified when the user changes the week count. */
  onWeeksChange?: (weeks: number) => void;
  /** Minimum selectable weeks. Defaults to 1. */
  minWeeks?: number;
  /** Maximum selectable weeks. Defaults to 12. */
  maxWeeks?: number;
}

export type DatePickerProps = SingleDatePickerProps | RangeDatePickerProps | WeekDatePickerProps;

function resolveRegion(props: {
  region?: DateRegion;
  dateFormat?: string;
  weekStartsOn?: WeekStartsOn;
}) {
  const region = props.region ?? 'EU';
  const defaults = REGION_DEFAULTS[region];
  return {
    region,
    format: props.dateFormat ?? defaults.dateFormat,
    weekStartsOn: (props.weekStartsOn ?? defaults.weekStartsOn) as WeekStartsOn,
  };
}

function safeFormat(date: Date | undefined | null, fmt: string): string {
  if (!date || !isValid(date)) return '';
  return formatDate(date, fmt);
}

function formatDisplay(
  value: Date | DateRange | null,
  fmt: string,
  region: DateRegion,
  formatValue?: CommonProps['formatValue'],
): string {
  if (formatValue) return formatValue(value, { format: fmt, region });
  if (!value) return '';
  if (value instanceof Date) return safeFormat(value, fmt);
  const from = safeFormat(value.from, fmt);
  const to = safeFormat(value.to, fmt);
  if (!from && !to) return '';
  return `${from} – ${to}`;
}

/**
 * ENG-837: typed entry accepts numeric shorthand — `15-08` fills in the
 * current year, and separators need not match the display format. See
 * `parseDateInput`.
 */
function tryParseSingle(text: string, fmt: string): Date | null {
  return parseDateInput(text, fmt);
}

/** Days in a range, counting both ends: a single day is 1. */
function spanInDays(a: Date, b: Date): number {
  return Math.abs(differenceInCalendarDays(b, a)) + 1;
}

function computeWeekRange(anchor: Date, weeks: number, weekStartsOn: WeekStartsOn): DateRange {
  const from = startOfWeek(anchor, { weekStartsOn });
  const lastWeekAnchor = addDays(from, (weeks - 1) * 7);
  const to = endOfWeek(lastWeekAnchor, { weekStartsOn });
  return { from, to };
}

function useControllable<T>(controlled: T | undefined, defaultValue: T): [T, (next: T) => void] {
  const [internal, setInternal] = React.useState<T>(defaultValue);
  const isControlled = controlled !== undefined;
  const value = isControlled ? controlled : internal;
  const setValue = React.useCallback(
    (next: T) => {
      if (!isControlled) setInternal(next);
    },
    [isControlled],
  );
  return [value, setValue];
}

const INPUT_CLASS =
  'h-full min-w-0 flex-1 bg-transparent px-2.5 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed';

function DatePicker(props: DatePickerProps) {
  const {
    region: _r,
    dateFormat: _df,
    weekStartsOn: _wso,
    numberOfMonths = 2,
    placeholder,
    disabled,
    className,
    id,
    name,
    autoFocus,
    defaultOpen,
    popoverAnchor = 'trigger',
    formatValue,
    disabledDates,
  } = props;
  const { region, format, weekStartsOn } = resolveRegion(props);
  const mode = props.mode ?? 'single';

  // ── Value (controlled + uncontrolled) ─────────────────────────────
  const defaultSingle = (props as SingleDatePickerProps).defaultValue ?? null;
  const defaultRange = (props as RangeDatePickerProps).defaultValue ?? null;
  const [singleValue, setSingleValueInternal] = useControllable<Date | null>(
    mode === 'single' ? (props as SingleDatePickerProps).value : undefined,
    defaultSingle,
  );
  const [rangeValue, setRangeValueInternal] = useControllable<DateRange | null>(
    mode !== 'single' ? (props as RangeDatePickerProps | WeekDatePickerProps).value : undefined,
    defaultRange,
  );

  const rangeProps = mode === 'range' ? (props as RangeDatePickerProps) : undefined;
  const fromLabel = rangeProps?.fromLabel ?? 'From';
  const toLabel = rangeProps?.toLabel ?? 'To';
  const requireApply = rangeProps?.requireApply ?? false;
  const applyLabel = rangeProps?.applyLabel ?? 'Apply';
  const maxDays = rangeProps?.maxDays;
  const fitsMaxDays = (a: Date, b: Date) => maxDays === undefined || spanInDays(a, b) <= maxDays;

  // ── Week count (controlled + uncontrolled) ────────────────────────
  const weekProps = mode === 'week' ? (props as WeekDatePickerProps) : undefined;
  const minWeeks = weekProps?.minWeeks ?? 1;
  const maxWeeks = weekProps?.maxWeeks ?? 12;
  const [weeks, setWeeksInternal] = useControllable<number>(
    weekProps?.weeks,
    Math.min(Math.max(weekProps?.defaultWeeks ?? 1, minWeeks), maxWeeks),
  );

  const setWeeks = (next: number) => {
    const clamped = Math.min(Math.max(next, minWeeks), maxWeeks);
    setWeeksInternal(clamped);
    weekProps?.onWeeksChange?.(clamped);
    // Recompute the selected range when weeks change so the visual stays in sync.
    if (mode === 'week' && rangeValue?.from) {
      const nextRange = computeWeekRange(rangeValue.from, clamped, weekStartsOn);
      setRangeValueInternal(nextRange);
      weekProps?.onValueChange?.(nextRange);
    }
  };

  // ── Open / close ─────────────────────────────────────────────────
  const [open, setOpen] = React.useState(defaultOpen ?? false);
  const rootRef = React.useRef<HTMLDivElement>(null);

  // ── Range selection in progress (range mode) ──────────────────────
  // react-day-picker's own range logic completes a one-day range on the FIRST
  // click (`addToRange` with min = 0 returns `{ from: day, to: day }`), which
  // used to close this picker after a single click. Range mode therefore
  // drives the selection itself: `rangeTarget` is the endpoint the next click
  // sets, and `pendingFrom` a start picked in this session whose finish is
  // still blank. A start that can keep the existing finish is committed
  // straight away instead; the pending start exists only when there is no
  // finish to keep, and lives here rather than in the value so a controlled
  // parent only ever receives complete ranges while the From field still
  // updates the moment the start is clicked.
  const [rangeTarget, setRangeTarget] = React.useState<DateRangeField>('from');
  const [pendingFrom, setPendingFrom] = React.useState<Date | null>(null);
  // With `requireApply` the clicks land in this draft — a copy of the value
  // taken each time the calendar opens — and only Apply hands it back.
  // Without it, each complete range is committed as it is clicked.
  const [draftRange, setDraftRange] = React.useState<DateRange | null>(rangeValue);
  const workingRange = requireApply && open ? draftRange : rangeValue;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      // Opened from the calendar icon: pick a start first.
      setRangeTarget('from');
      setDraftRange(rangeValue);
    } else {
      // Closing discards a half-picked range, and an unapplied draft; the
      // fields fall back to the value.
      setPendingFrom(null);
    }
  };

  /** Range mode: open the calendar with the next click aimed at `field`. */
  const openRangeFor = (field: DateRangeField) => {
    if (disabled) return;
    const start = pendingFrom ?? workingRange?.from ?? null;
    // An end needs a start to anchor to; without one the first click picks the start.
    setRangeTarget(field === 'to' && start !== null ? 'to' : 'from');
    // Re-aiming a calendar that is already open keeps the draft picked so far.
    if (!open) setDraftRange(rangeValue);
    setOpen(true);
  };

  // ── Input draft (typing) — single mode only ──────────────────────
  const displayValue =
    mode === 'single'
      ? formatDisplay(singleValue, format, region, formatValue)
      : mode === 'week'
        ? formatDisplay(rangeValue, format, region, formatValue)
        : '';

  const [draft, setDraft] = React.useState<string | null>(null);
  React.useEffect(() => {
    // Reset draft whenever the displayed value changes externally.
    setDraft(null);
  }, [displayValue]);

  const inputValue = draft ?? displayValue;

  const commitDraft = () => {
    if (mode !== 'single' || draft === null) {
      setDraft(null);
      return;
    }
    if (draft.trim() === '') {
      setSingleValueInternal(null);
      (props as SingleDatePickerProps).onValueChange?.(null);
      setDraft(null);
      return;
    }
    const parsed = tryParseSingle(draft, format);
    if (parsed) {
      setSingleValueInternal(parsed);
      (props as SingleDatePickerProps).onValueChange?.(parsed);
    }
    setDraft(null);
  };

  // ── Calendar interaction ─────────────────────────────────────────
  const handleSingleSelect = (next: Date | undefined) => {
    const v = next ?? null;
    setSingleValueInternal(v);
    (props as SingleDatePickerProps).onValueChange?.(v);
    if (v) setOpen(false);
  };

  const commitRange = (next: DateRange) => {
    if (requireApply) {
      setDraftRange(next);
      return;
    }
    setRangeValueInternal(next);
    rangeProps?.onValueChange?.(next);
  };

  const handleRangeDayClick = (day: Date) => {
    if (rangeTarget === 'from') {
      setRangeTarget('to');
      if (
        workingRange &&
        differenceInCalendarDays(day, workingRange.to) <= 0 &&
        fitsMaxDays(day, workingRange.to)
      ) {
        // A range is already set and the finish still comes on or after the
        // new start, so one click moves only the start. The picker stays open
        // aimed at the finish: a second click changes it exactly as it would
        // from a fresh state, and closing leaves the moved start in place
        // (unless `requireApply`, where closing discards the whole draft).
        commitRange({ from: day, to: workingRange.to });
        setPendingFrom(null);
        return;
      }
      // No finish to keep — none set yet, the new start lands after it, or
      // keeping it would run past `maxDays`. Hold the start and blank the
      // finish until the second click lands.
      setPendingFrom(day);
      return;
    }
    const anchor = pendingFrom ?? workingRange?.from ?? null;
    if (anchor === null) {
      // Aimed at the finish with no start to anchor to: treat this as the start.
      setPendingFrom(day);
      return;
    }
    // The finish click — or the only click when opened from the To field. The
    // range always runs between the two designated days, whichever came first.
    const next: DateRange =
      differenceInCalendarDays(day, anchor) < 0
        ? { from: day, to: anchor }
        : { from: anchor, to: day };
    commitRange(next);
    setPendingFrom(null);
    if (requireApply) {
      // The draft is complete and waits for Apply; a further click picks a
      // new start, as it would on reopening.
      setRangeTarget('from');
    } else {
      setOpen(false);
    }
  };

  // Only a complete draft within `maxDays` applies; a start still waiting
  // for its finish does not.
  const canApply =
    pendingFrom === null && draftRange !== null && fitsMaxDays(draftRange.from, draftRange.to);

  const applyRange = () => {
    if (!canApply || draftRange === null) return;
    setRangeValueInternal(draftRange);
    rangeProps?.onValueChange?.(draftRange);
    handleOpenChange(false);
  };

  const handleWeekDayClick = (day: Date) => {
    const range = computeWeekRange(day, weeks, weekStartsOn);
    setRangeValueInternal(range);
    weekProps?.onValueChange?.(range);
    setOpen(false);
  };

  // ── Picker payload per mode ──────────────────────────────────────
  // While the next click sets a range's finish, no day may stretch the range
  // past `maxDays`, whichever side of the start it lands on.
  const capAnchor =
    mode === 'range' && rangeTarget === 'to' ? (pendingFrom ?? workingRange?.from ?? null) : null;
  const disabledDays =
    capAnchor === null || maxDays === undefined
      ? disabledDates
      : [
          ...(disabledDates === undefined
            ? []
            : Array.isArray(disabledDates)
              ? disabledDates
              : [disabledDates]),
          { before: addDays(capAnchor, -(maxDays - 1)) },
          { after: addDays(capAnchor, maxDays - 1) },
        ];
  // `exactOptionalPropertyTypes` requires conditional spreading rather than
  // passing `undefined` for optional fields like `defaultMonth` / `disabled`.
  const baseCommon = {
    numberOfMonths,
    weekStartsOn,
    ...(disabledDays !== undefined ? { disabled: disabledDays } : {}),
  } as const;
  const calendar = (() => {
    if (mode === 'single') {
      return (
        <Calendar
          mode="single"
          selected={singleValue ?? undefined}
          onSelect={handleSingleSelect}
          {...baseCommon}
          {...(singleValue ? { defaultMonth: singleValue } : {})}
        />
      );
    }
    if (mode === 'range') {
      const selected = pendingFrom
        ? { from: pendingFrom, to: undefined }
        : workingRange
          ? { from: workingRange.from, to: workingRange.to }
          : undefined;
      const month = pendingFrom ?? workingRange?.from;
      return (
        <Calendar
          mode="range"
          selected={selected}
          onDayClick={handleRangeDayClick}
          // Selection is driven by onDayClick (see handleRangeDayClick). The
          // no-op onSelect keeps react-day-picker reading `selected` from us
          // instead of running its own range logic.
          onSelect={() => undefined}
          {...baseCommon}
          {...(month ? { defaultMonth: month } : {})}
        />
      );
    }
    // week mode — present as a range visually but click selects a whole week.
    return (
      <div className="flex flex-col gap-2">
        <WeekCountControl
          weeks={weeks}
          minWeeks={minWeeks}
          maxWeeks={maxWeeks}
          onChange={setWeeks}
        />
        <Calendar
          mode="range"
          selected={rangeValue ? { from: rangeValue.from, to: rangeValue.to } : undefined}
          onDayClick={handleWeekDayClick}
          // `onSelect` is required on range mode but we drive selection via
          // onDayClick to snap to whole weeks. The empty handler keeps the
          // visual selection stable.
          onSelect={() => undefined}
          showWeekNumber
          {...baseCommon}
          {...(rangeValue?.from ? { defaultMonth: rangeValue.from } : {})}
        />
      </div>
    );
  })();

  const inputReadOnly = mode !== 'single';

  // Range mode shows the start and end in their own fields. While a start is
  // pending the From field shows it and the To field goes blank, so the user
  // sees each click land before the range is complete — and with
  // `requireApply` the fields show the draft until the calendar closes.
  const rangeFromDate = pendingFrom ?? workingRange?.from ?? null;
  const rangeToDate = pendingFrom ? null : (workingRange?.to ?? null);

  const renderRangeInput = (field: DateRangeField) => {
    const isFrom = field === 'from';
    const label = isFrom ? fromLabel : toLabel;
    return (
      <input
        data-slot="date-picker-input"
        data-field={field}
        data-active={open && rangeTarget === field ? '' : undefined}
        id={isFrom ? id : id ? `${id}-to` : undefined}
        name={isFrom ? name : name ? `${name}-to` : undefined}
        type="text"
        value={formatDisplay(isFrom ? rangeFromDate : rangeToDate, format, region, formatValue)}
        placeholder={label}
        readOnly
        disabled={disabled}
        // oxlint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={isFrom ? autoFocus : undefined}
        aria-label={label}
        onClick={() => openRangeFor(field)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (open) handleOpenChange(false);
            else openRangeFor(field);
          } else if (e.key === 'Escape' && open) {
            e.preventDefault();
            handleOpenChange(false);
          }
        }}
        className={cn(
          INPUT_CLASS,
          'data-active:bg-muted/60 cursor-pointer',
          isFrom && 'rounded-l-md',
        )}
      />
    );
  };

  const isRange = mode === 'range';

  const control = (
    <div
      ref={rootRef}
      data-slot="date-picker"
      data-mode={mode}
      role={isRange ? 'group' : undefined}
      aria-label={isRange ? props['aria-label'] : undefined}
      aria-labelledby={isRange ? props['aria-labelledby'] : undefined}
      aria-describedby={isRange ? props['aria-describedby'] : undefined}
      className={cn(
        'border-input bg-transparent focus-within:border-ring focus-within:ring-ring/50 aria-disabled:opacity-50 dark:bg-input/30 inline-flex h-9 w-full min-w-0 items-center rounded-md border shadow-xs transition-[color,box-shadow] focus-within:ring-3',
        className,
      )}
      aria-disabled={disabled || undefined}
    >
      {isRange ? (
        <>
          {renderRangeInput('from')}
          <HugeiconsIcon
            icon={ArrowRight01Icon}
            className="text-muted-foreground size-3.5 shrink-0"
            aria-hidden
          />
          {renderRangeInput('to')}
        </>
      ) : (
        <input
          data-slot="date-picker-input"
          id={id}
          name={name}
          type="text"
          value={inputValue}
          placeholder={placeholder ?? format.toLowerCase()}
          readOnly={inputReadOnly}
          disabled={disabled}
          // oxlint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          aria-label={props['aria-label']}
          aria-labelledby={props['aria-labelledby']}
          aria-describedby={props['aria-describedby']}
          onChange={(e) => {
            if (mode === 'single') setDraft(e.target.value);
          }}
          onClick={() => {
            // A read-only field has nothing to type into, so one click opens it.
            if (inputReadOnly && !disabled) setOpen(true);
          }}
          onDoubleClick={() => {
            if (!disabled) setOpen(true);
          }}
          onBlur={commitDraft}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (mode === 'single' && draft !== null) {
                commitDraft();
              } else {
                setOpen((v) => !v);
              }
            } else if (e.key === 'Escape') {
              if (open) {
                e.preventDefault();
                setOpen(false);
              } else if (draft !== null) {
                setDraft(null);
              }
            }
          }}
          className={cn(INPUT_CLASS, 'w-full rounded-md', inputReadOnly && 'cursor-pointer')}
        />
      )}
      <PopoverTrigger asChild>
        <button
          type="button"
          data-slot="date-picker-trigger"
          aria-label="Open date picker"
          disabled={disabled}
          className="text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-ring/50 inline-flex h-full w-9 shrink-0 items-center justify-center rounded-r-md transition-colors focus-visible:ring-3 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          <HugeiconsIcon icon={Calendar03Icon} className="size-4" aria-hidden />
        </button>
      </PopoverTrigger>
    </div>
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      {popoverAnchor === 'field' ? <PopoverAnchor asChild>{control}</PopoverAnchor> : control}
      <PopoverContent
        data-slot="date-picker-content"
        align="start"
        className="w-auto p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          // Read-only fields open the calendar on click, so a click on one
          // while it is open must re-aim the picker, not dismiss and reopen it.
          const target = e.detail.originalEvent.target;
          if (inputReadOnly && target instanceof Node && rootRef.current?.contains(target)) {
            e.preventDefault();
          }
        }}
      >
        {calendar}
        {mode === 'range' && (requireApply || maxDays !== undefined) && (
          <div
            data-slot="date-picker-footer"
            className="border-foreground/10 flex items-center justify-end gap-3 border-t px-3 py-2"
          >
            {maxDays !== undefined && (
              <p data-slot="date-picker-hint" className="text-muted-foreground me-auto text-xs">
                {rangeProps?.maxDaysHint ?? `A range is at most ${maxDays} days`}
              </p>
            )}
            {requireApply && (
              <Button type="button" size="sm" disabled={!canApply} onClick={applyRange}>
                {applyLabel}
              </Button>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface WeekCountControlProps {
  weeks: number;
  minWeeks: number;
  maxWeeks: number;
  onChange: (weeks: number) => void;
}

function WeekCountControl({ weeks, minWeeks, maxWeeks, onChange }: WeekCountControlProps) {
  return (
    <div
      data-slot="date-picker-weeks"
      className="border-foreground/10 flex items-center justify-between gap-2 border-b px-3 py-2"
    >
      <label
        htmlFor="date-picker-weeks-input"
        className="text-foreground/70 text-xs font-medium tracking-wider uppercase"
      >
        Weeks
      </label>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Decrease weeks"
          onClick={() => onChange(weeks - 1)}
          disabled={weeks <= minWeeks}
          className="border-input text-foreground/70 hover:bg-muted inline-flex h-6 w-6 items-center justify-center rounded-md border transition-colors disabled:pointer-events-none disabled:opacity-40"
        >
          −
        </button>
        <input
          id="date-picker-weeks-input"
          type="number"
          min={minWeeks}
          max={maxWeeks}
          value={weeks}
          onChange={(e) => {
            const next = Number(e.target.value);
            if (Number.isFinite(next)) onChange(next);
          }}
          className="border-input focus-visible:ring-ring/50 h-6 w-10 rounded-md border bg-transparent px-1 text-center text-xs tabular-nums outline-none focus-visible:ring-3"
        />
        <button
          type="button"
          aria-label="Increase weeks"
          onClick={() => onChange(weeks + 1)}
          disabled={weeks >= maxWeeks}
          className="border-input text-foreground/70 hover:bg-muted inline-flex h-6 w-6 items-center justify-center rounded-md border transition-colors disabled:pointer-events-none disabled:opacity-40"
        >
          +
        </button>
      </div>
    </div>
  );
}

export { DatePicker };
